import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Plus, Trash } from "@phosphor-icons/react";

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const load = () => api.get("/goals").then((r) => setGoals(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await api.post("/goals", { title, description });
      setTitle(""); setDescription("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const update = async (id, fields) => {
    await api.patch(`/goals/${id}`, fields);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/goals/${id}`);
    load();
  };

  return (
    <div className="space-y-10">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-2">Goals</p>
        <h1 className="font-serif text-4xl md:text-5xl font-medium">Intentions for the season.</h1>
      </div>

      <form onSubmit={add} className="bg-white rounded-3xl border border-border p-6 md:p-8 soft-shadow space-y-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="A goal worth becoming…"
               data-testid="goal-title"
               className="w-full px-5 py-3 rounded-2xl border border-border bg-sand focus:outline-none focus:ring-2 focus:ring-sage" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Why does this matter to you?"
                  data-testid="goal-description"
                  className="w-full min-h-[80px] rounded-2xl border border-border bg-sand p-4 focus:outline-none focus:ring-2 focus:ring-sage" />
        <div className="flex justify-end">
          <button type="submit" data-testid="goal-add"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-moss text-white hover:bg-mossDark">
            <Plus size={16} /> Add goal
          </button>
        </div>
      </form>

      <div className="space-y-4">
        {goals.length === 0 ? (
          <p className="text-muted-foreground">Set your first intention above.</p>
        ) : goals.map((g) => (
          <div key={g.id} data-testid={`goal-${g.id}`}
               className="bg-white rounded-3xl border border-border p-6 soft-shadow">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <h3 className="font-serif text-xl">{g.title}</h3>
                {g.description && <p className="text-muted-foreground text-sm mt-1">{g.description}</p>}
              </div>
              <button onClick={() => remove(g.id)} data-testid={`goal-delete-${g.id}`}
                      className="text-muted-foreground hover:text-destructive">
                <Trash size={18} />
              </button>
            </div>
            <div className="flex items-center gap-4">
              <input type="range" min="0" max="100" value={g.progress}
                     onChange={(e) => update(g.id, { progress: parseInt(e.target.value) })}
                     data-testid={`goal-progress-${g.id}`}
                     className="flex-1 accent-moss" />
              <span className="font-serif text-xl w-14 text-right">{g.progress}%</span>
            </div>
            <div className="relative h-1.5 rounded-full bg-cream overflow-hidden mt-3">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-moss to-sage rounded-full transition-all"
                   style={{ width: `${g.progress}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
